import { Component, Input } from '@angular/core';
import { PermitHistoryEntry } from '../../../models/permit-detail.model';

/**
 * Renders a permit's status timeline. Used on the CRM detail page (staff, with
 * actor) and the applicant magic-link view (visible entries only, no actor).
 */
@Component({
    selector: 'app-permit-history',
    templateUrl: './permit-history.component.html',
    styleUrls: ['./permit-history.component.scss'],
    standalone: false,
})
export class PermitHistoryComponent {
    @Input() public history: PermitHistoryEntry[] | null | undefined = null;
    /** Show who made each change (staff view). */
    @Input() public showActor = false;

    /**
     * Newest first. The backend orders the timeline chronologically (and relies on that
     * order elsewhere), so the reverse is purely for display: whoever opens a permit
     * wants its latest action at the top, not to scroll past its whole life to reach it.
     */
    public get orderedHistory(): PermitHistoryEntry[] {
        return this.history ? [...this.history].reverse() : [];
    }

    private static readonly TRANSITION_LABELS: Record<string, string> = {
        submit: 'Submitted',
        issue: 'Issued automatically',
        review: 'Marked in review',
        request_info: 'More information requested',
        provide_info: 'Information provided',
        approve: 'Approved',
        manual_approve: 'Manually approved',
        request_payment: 'Payment requested',
        pay: 'Payment received',
        activate: 'Activated',
        deny: 'Denied',
        revoke: 'Revoked',
        // Without this a refund falls through to the STATUS label and reads as
        // "Active" - the note underneath says a refund happened while the heading
        // says nothing did.
        refund: 'Refunded',
        // Same reason: an extension keeps the permit active, so without its own label
        // the entry would read "Active" while the note says it was extended.
        extend: 'Extended',
        // A staff edit keeps the status the same, so without its own label the entry
        // would read as the current status while the note lists what actually changed.
        edited: 'Details edited',
        // An internal staff note - no status change, just something recorded against the
        // application (a call, a consultation, a reason). Its own label, or it would read
        // as the current status with the note beneath it.
        note: 'Internal note',
        expire: 'Expired',
        clan_area_approved: 'Clan area approved',
        clan_area_rejected: 'Clan area rejected',
        clan_area_pending: 'Clan area reopened',
        // Staff recorded their own check; the host confirming via the emailed link is
        // its own line, so the timeline shows which one happened.
        verification_recorded: 'Verification recorded by staff',
        verification_withdrawn: 'Verification withdrawn',
        host_confirmed: 'Confirmed by host',
    };

    private static readonly STATUS_LABELS: Record<string, string> = {
        submitted: 'Submitted',
        in_review: 'In review',
        needs_info: 'Needs info',
        approved: 'Approved',
        pending_payment: 'Pending payment',
        active: 'Active',
        expired: 'Expired',
        denied: 'Denied',
        revoked: 'Revoked',
    };

    public label(entry: PermitHistoryEntry): string {
        return (
            (entry.transition && PermitHistoryComponent.TRANSITION_LABELS[entry.transition]) ||
            PermitHistoryComponent.STATUS_LABELS[entry.status] ||
            entry.status
        );
    }
}
